// ABOUTME: Modal component for previewing pinned insights.
// ABOUTME: Reconstructs insight visualization from stored JSON data.
// ABOUTME: Uses React Portal to render at document root for proper z-index stacking.

import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BarChart3, Table2, FileText, LayoutGrid, PieChart, LineChart } from 'lucide-react';
import { ChartRenderer } from '@/components/investigation/ChartRenderer';
import { PythonVizRenderer } from '@/components/investigation/PythonVizRenderer';
import type { PinnedItem, PinnedItemType } from '@/types';
import type { StructuredInsight, ChartConfig, TableConfig, SummaryConfig, KeyMetric, BulletPoint, PythonVizConfig } from '@/types/sdk';

interface InsightPreviewModalProps {
  item: PinnedItem | null;
  onClose: () => void;
}

// Get the appropriate icon component for each pin type
function getTypeIcon(type: PinnedItemType) {
  switch (type) {
    case 'chart':
      return BarChart3;
    case 'table':
      return Table2;
    case 'summary':
    case 'text':
      return FileText;
    case 'chart-table':
      return LayoutGrid;
    case 'table-summary':
      return Table2;
    case 'metric-card':
      return PieChart;
    case 'python-viz':
    case 'python-viz-with-summary':
      return LineChart;
    default:
      return FileText;
  }
}

// Get a color for each type
function getTypeColor(type: PinnedItemType): string {
  switch (type) {
    case 'chart':
      return 'text-primary-600';
    case 'table':
      return 'text-success';
    case 'summary':
    case 'text':
      return 'text-accent-600';
    case 'chart-table':
      return 'text-violet-600';
    case 'table-summary':
      return 'text-primary-500';
    case 'metric-card':
      return 'text-error';
    case 'python-viz':
    case 'python-viz-with-summary':
      return 'text-primary-400';
    default:
      return 'text-surface-600';
  }
}

// Get human-readable type name
function getTypeName(type: PinnedItemType): string {
  switch (type) {
    case 'chart':
      return 'Chart';
    case 'table':
      return 'Table';
    case 'summary':
      return 'Summary';
    case 'text':
      return 'Text';
    case 'chart-table':
      return 'Chart + Table';
    case 'table-summary':
      return 'Table + Summary';
    case 'metric-card':
      return 'Metric Card';
    case 'python-viz':
      return 'Visualization';
    case 'python-viz-with-summary':
      return 'Viz + Summary';
    default:
      return 'Insight';
  }
}

export function InsightPreviewModal({ item, onClose }: InsightPreviewModalProps) {
  // Handle escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (item) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [item, handleEscape]);

  if (!item) return null;

  const Icon = getTypeIcon(item.type);
  const iconColor = getTypeColor(item.type);
  const typeName = getTypeName(item.type);

  // Try to extract insight data from item.data
  const insightData = item.data as StructuredInsight | undefined;

  // Use portal to render at document root, escaping any parent stacking contexts
  return createPortal(
    <AnimatePresence>
      {item && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed inset-4 md:inset-8 lg:inset-16 z-[9999] flex items-center justify-center"
          >
            <div className="w-full max-w-4xl max-h-full bg-surface-0 border border-surface-200 rounded-sm shadow-2xl flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-surface-200">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-sm bg-surface-100 ${iconColor}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-surface-800">
                      {item.title}
                    </h2>
                    <p className="text-xs text-surface-500">
                      {typeName} • Pinned {new Date(item.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-sm bg-surface-100 hover:bg-surface-200 transition-colors cursor-pointer"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-surface-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {insightData ? (
                  <InsightContent insight={insightData} />
                ) : (
                  <div className="text-center py-12 text-surface-500">
                    <p>No preview data available for this insight.</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Main content renderer for insights
function InsightContent({ insight }: { insight: StructuredInsight }) {
  // Show insight overview if available
  const overview = insight.insight_overview;

  return (
    <div className="space-y-6">
      {/* Overview */}
      {overview && (
        <div className="text-sm text-surface-600 leading-relaxed">
          {overview}
        </div>
      )}

      {/* Key Metrics */}
      {insight.key_metrics && insight.key_metrics.length > 0 && (
        <KeyMetricsSection metrics={insight.key_metrics} />
      )}

      {/* Python Visualization */}
      {insight.python_viz && (
        <PythonVizSection pythonViz={insight.python_viz} />
      )}

      {/* Chart */}
      {insight.chart && (
        <ChartSection chart={insight.chart} />
      )}

      {/* Table */}
      {insight.table && (
        <TableSection table={insight.table} />
      )}

      {/* Summary */}
      {insight.summary && (
        <SummarySection summary={insight.summary} />
      )}
    </div>
  );
}

// Key Metrics Section
function KeyMetricsSection({ metrics }: { metrics: KeyMetric[] }) {
  const sentimentColors = {
    positive: 'text-success',
    negative: 'text-error',
    neutral: 'text-surface-600',
    warning: 'text-warning',
    critical: 'text-error',
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {metrics.map((metric, index) => (
        <div
          key={index}
          className="rounded-sm bg-surface-50 border border-surface-200 p-4"
        >
          <div className="text-xs text-surface-500 uppercase tracking-wider mb-1">
            {metric.label}
          </div>
          <div className={`text-2xl font-semibold ${sentimentColors[metric.sentiment]}`}>
            {metric.display_value}
          </div>
          {metric.change && (
            <div className="flex items-center gap-1 mt-2">
              <span
                className={`text-sm ${
                  metric.change.sentiment === 'positive'
                    ? 'text-success'
                    : metric.change.sentiment === 'negative'
                    ? 'text-error'
                    : 'text-surface-500'
                }`}
              >
                {metric.change.direction === 'up' ? '↑' : metric.change.direction === 'down' ? '↓' : '→'}
                {metric.change.value > 0 ? '+' : ''}
                {metric.change.value}%
              </span>
              {metric.change.period_label && (
                <span className="text-xs text-surface-500">
                  {metric.change.period_label}
                </span>
              )}
            </div>
          )}
          {metric.context && (
            <div className="text-xs text-surface-500 mt-2">{metric.context}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// Helper to check if a value is numeric (number or numeric string)
function isNumericValue(val: unknown): boolean {
  if (typeof val === 'number') return true;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    return !isNaN(num) && isFinite(num);
  }
  return false;
}

// Helper to convert value to number
function toNumber(val: unknown): number {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  }
  return 0;
}

// Detect label and value keys from chart data
function detectChartDataKeys(data: Record<string, unknown>[]): { labelKey: string; valueKey: string } {
  if (!data || data.length === 0) {
    return { labelKey: 'label', valueKey: 'value' };
  }

  const firstItem = data[0];
  const keys = Object.keys(firstItem);

  // Find the label key (first non-numeric string field, preferring 'label' or 'name')
  let labelKey = 'label';
  if (!('label' in firstItem) || typeof firstItem.label !== 'string' || isNumericValue(firstItem.label)) {
    if ('name' in firstItem && typeof firstItem.name === 'string' && !isNumericValue(firstItem.name)) {
      labelKey = 'name';
    } else {
      // Find first string key that's not a number (avoid date strings that are numeric)
      const stringKey = keys.find(k => {
        const val = firstItem[k];
        return typeof val === 'string' && !isNumericValue(val);
      });
      // If no pure string, try any string key
      if (stringKey) {
        labelKey = stringKey;
      } else {
        const anyStringKey = keys.find(k => typeof firstItem[k] === 'string');
        if (anyStringKey) labelKey = anyStringKey;
      }
    }
  }

  // Find the value key (first numeric field, preferring 'value')
  let valueKey = 'value';
  if (!('value' in firstItem) || !isNumericValue(firstItem.value)) {
    // Look for any numeric value (including string numbers)
    const numericKey = keys.find(k => isNumericValue(firstItem[k]) && k !== labelKey);
    if (numericKey) valueKey = numericKey;
  }

  return { labelKey, valueKey };
}

// Chart Section -- handles both single-series and multi-series data
function ChartSection({ chart }: { chart: ChartConfig }) {
  // Detect multi-series: multiple numeric fields, no standard label/value pair
  const firstItem = chart.data[0];
  let isMultiSeries = false;

  if (firstItem) {
    const keys = Object.keys(firstItem);
    const numericKeys = keys.filter(k => isNumericValue(firstItem[k]));
    const hasStandardKeys = 'label' in firstItem && 'value' in firstItem;
    // Multi-series: more than one numeric field and no standard label/value pair
    isMultiSeries = numericKeys.length > 1 && !hasStandardKeys;
  }

  const chartType = chart.type === 'bar' || chart.type === 'line' || chart.type === 'area'
    ? chart.type
    : 'bar';

  if (isMultiSeries) {
    return (
      <div className="rounded-sm bg-surface-50 border border-surface-200 p-4">
        <ChartRenderer
          rawData={chart.data as Record<string, any>[]}
          type={chartType}
          height={280}
        />
        {chart.x_axis?.label && (
          <div className="text-xs text-surface-500 text-center mt-2">
            {chart.x_axis.label}
          </div>
        )}
      </div>
    );
  }

  // Single-series path: detect label/value keys and normalize
  const { labelKey, valueKey } = detectChartDataKeys(chart.data as Record<string, unknown>[]);

  const chartData = chart.data.map((d: Record<string, unknown>) => {
    const rawLabel = d[labelKey] ?? d.label ?? d.name ?? '';
    const label = String(rawLabel);
    const rawValue = d[valueKey] ?? d.value ?? 0;
    const value = toNumber(rawValue);
    return { ...d, label, value };
  });

  return (
    <div className="rounded-sm bg-surface-50 border border-surface-200 p-4">
      <ChartRenderer
        data={chartData}
        type={chartType}
        height={280}
        xAxisKey="label"
        yAxisKey="value"
      />
      {chart.x_axis?.label && (
        <div className="text-xs text-surface-500 text-center mt-2">
          {chart.x_axis.label}
        </div>
      )}
    </div>
  );
}

// Table Section
function TableSection({ table }: { table: TableConfig }) {
  const displayRows = table.rows.slice(0, table.max_display_rows || 10);
  const hasMore = table.rows.length > displayRows.length;

  return (
    <div className="rounded-sm bg-surface-50 border border-surface-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-200 bg-surface-100">
              {table.show_row_numbers && (
                <th className="px-4 py-3 text-left text-xs text-surface-500 font-medium">
                  #
                </th>
              )}
              {table.columns.map((col) => (
                <th
                  key={col.id}
                  className={`px-4 py-3 text-xs text-surface-500 font-medium ${
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                  }`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-surface-100 last:border-0 hover:bg-surface-100">
                {table.show_row_numbers && (
                  <td className="px-4 py-3 text-surface-500">{rowIndex + 1}</td>
                )}
                {table.columns.map((col) => (
                  <td
                    key={col.id}
                    className={`px-4 py-3 text-surface-600 ${
                      col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                    }`}
                  >
                    {formatCellValue(row[col.id], col.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="text-xs text-surface-500 text-center py-3 border-t border-surface-200">
          Showing {displayRows.length} of {table.rows.length} rows
        </div>
      )}
    </div>
  );
}

function formatCellValue(value: unknown, type?: string): string {
  if (value === null || value === undefined) return '-';

  switch (type) {
    case 'currency':
      return typeof value === 'number'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
        : String(value);
    case 'percentage':
      return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : String(value);
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    case 'date':
      return value instanceof Date ? value.toLocaleDateString() : String(value);
    default:
      return String(value);
  }
}

// Python Visualization Section
function PythonVizSection({ pythonViz }: { pythonViz: PythonVizConfig }) {
  return (
    <div className="rounded-sm bg-surface-50 border border-surface-200 overflow-hidden">
      <PythonVizRenderer pythonViz={pythonViz} />
    </div>
  );
}

// Summary Section
function SummarySection({ summary }: { summary: SummaryConfig }) {
  return (
    <div className="rounded-sm bg-surface-50 border border-surface-200 p-4 space-y-3">
      {summary.introduction && (
        <p className="text-sm text-surface-600 leading-relaxed">
          {summary.introduction}
        </p>
      )}

      {summary.bullet_points && summary.bullet_points.length > 0 && (
        <ul className="space-y-2">
          {summary.bullet_points.map((point, index) => (
            <BulletPointItem key={index} point={point} />
          ))}
        </ul>
      )}

      {summary.conclusion && (
        <p className="text-sm text-surface-600 leading-relaxed pt-3 border-t border-surface-200">
          {summary.conclusion}
        </p>
      )}
    </div>
  );
}

function BulletPointItem({ point }: { point: BulletPoint }) {
  const emphasisStyles = {
    normal: 'text-surface-600',
    highlight: 'text-primary-600 font-medium',
    critical: 'text-error font-medium',
  };

  return (
    <li className="flex items-start gap-2">
      <span className="text-surface-500 mt-1">•</span>
      <div>
        <span className={`text-sm ${emphasisStyles[point.emphasis || 'normal']}`}>
          {point.text}
        </span>
        {point.sub_points && point.sub_points.length > 0 && (
          <ul className="mt-1 ml-4 space-y-1">
            {point.sub_points.map((subPoint, index) => (
              <li key={index} className="text-xs text-surface-500 flex items-start gap-1">
                <span>◦</span>
                <span>{subPoint}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
